// WeatherDisplay.tsx
// Reusable weather information display component for checkpoints, refuels, expenses, and travel points
// Supports multiple display variants: compact (tables), small (cards), and full (detail views)

import React, { useMemo } from "react";
import {
  Group,
  Stack,
  Text,
  Tooltip,
  Paper,
  Box,
  ThemeIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useUserProfileContext } from "@/contexts/UserProfileContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface WeatherData {
  weatherTempC?: number | null;
  weatherFeelsLikeC?: number | null;
  weatherConditionCode?: string | null;
  weatherConditionIcon?: string | null;
  weatherDescription?: string | null;
  weatherHumidityPct?: number | null;
  weatherPressureHpa?: number | null;
  weatherCloudPct?: number | null;
  weatherVisibilityM?: number | null;
  weatherWindSpeedMps?: number | null;
  weatherWindDirDeg?: number | null;
  weatherPrecipMm?: number | null;
  weatherUvIndex?: number | null;
  weatherProvider?: string | null;
  weatherFetchedAt?: string | null;
}

export type WeatherDisplayVariant = "compact" | "small" | "full";

export interface WeatherDisplayProps {
  data: WeatherData;
  variant?: WeatherDisplayVariant;
  showIcon?: boolean;
  showFeelsLike?: boolean;
  showWind?: boolean;
  showHumidity?: boolean;
  className?: string;
}

// ============================================
// Weather Icon Mapping (using Tabler Icons)
// ============================================

type TablerIconName =
  | "Sun"
  | "Moon"
  | "Cloud"
  | "CloudRain"
  | "CloudStorm"
  | "CloudSnow"
  | "CloudFog"
  | "Wind"
  | "Temperature"
  | "Droplet"
  | "Eye"
  | "Snowflake"
  | "Gauge";

interface WeatherIconConfig {
  icon: TablerIconName;
  color: string;
}

const getWeatherIcon = (conditionCode?: string | null): WeatherIconConfig => {
  if (!conditionCode) {
    return { icon: "Cloud", color: "gray" };
  }

  const code = conditionCode.toLowerCase();

  if (code.includes("clear") || code.includes("sunny")) {
    return { icon: "Sun", color: "orange" };
  }
  if (code.includes("partly") || code.includes("scattered")) {
    return { icon: "Cloud", color: "blue" };
  }
  if (code.includes("thunder") || code.includes("storm")) {
    return { icon: "CloudStorm", color: "violet" };
  }
  if (code.includes("snow") || code.includes("blizzard") || code.includes("sleet")) {
    return { icon: "CloudSnow", color: "cyan" };
  }
  if (code.includes("hail") || code.includes("ice") || code.includes("freezing")) {
    return { icon: "Snowflake", color: "teal" };
  }
  if (code.includes("rain") || code.includes("shower") || code.includes("drizzle")) {
    return { icon: "CloudRain", color: "blue" };
  }
  if (code.includes("fog") || code.includes("haze") || code.includes("mist") || code.includes("smoke")) {
    return { icon: "CloudFog", color: "gray" };
  }
  if (code.includes("cloud") || code.includes("overcast")) {
    return { icon: "Cloud", color: "gray" };
  }
  if (code.includes("wind")) {
    return { icon: "Wind", color: "teal" };
  }

  return { icon: "Cloud", color: "gray" };
};

const getWindDirection = (degrees?: number | null): string => {
  if (degrees === null || degrees === undefined) return "";
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

// ============================================
// Helper Functions
// ============================================

const celsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9) / 5 + 32;
};

const mpsToKmh = (mps: number): number => {
  return mps * 3.6;
};

const mpsToMph = (mps: number): number => {
  return mps * 2.237;
};

const metersToKm = (meters: number): number => {
  return meters / 1000;
};

const metersToMiles = (meters: number): number => {
  return meters / 1609.344;
};

const formatTemperature = (
  tempC: number | null | undefined,
  useCelsius: boolean
): string => {
  if (tempC === null || tempC === undefined) return "—";
  if (useCelsius) {
    return `${Math.round(tempC)}°C`;
  } else {
    return `${Math.round(celsiusToFahrenheit(tempC))}°F`;
  }
};

const formatWindSpeed = (
  speedMps: number | null | undefined,
  useMetric: boolean
): string => {
  if (speedMps === null || speedMps === undefined) return "—";
  if (useMetric) {
    return `${Math.round(mpsToKmh(speedMps))} km/h`;
  } else {
    return `${Math.round(mpsToMph(speedMps))} mph`;
  }
};

const formatVisibility = (
  visibilityM: number | null | undefined,
  useMetric: boolean
): string => {
  if (visibilityM === null || visibilityM === undefined) return "—";
  if (useMetric) {
    const km = metersToKm(visibilityM);
    return km >= 1 ? `${km.toFixed(1)} km` : `${Math.round(visibilityM)} m`;
  } else {
    const miles = metersToMiles(visibilityM);
    return `${miles.toFixed(1)} mi`;
  }
};

// ============================================
// Sub-Components
// ============================================

interface WeatherIconDisplayProps {
  conditionCode?: string | null;
  size?: number;
  description?: string | null;
}

const WeatherIconDisplay: React.FC<WeatherIconDisplayProps> = ({
  conditionCode,
  size = 20,
  description,
}) => {
  const { icon, color } = getWeatherIcon(conditionCode);

  return (
    <Tooltip label={description || conditionCode} disabled={!description && !conditionCode}>
      <Box style={{ display: "flex", alignItems: "center", lineHeight: 1 }}>
        <AppIcon iconName={icon} size={size} color={color} />
      </Box>
    </Tooltip>
  );
};

// ============================================
// Main Component
// ============================================

const WeatherDisplayComponent: React.FC<WeatherDisplayProps> = ({
  data,
  variant = "small",
  showIcon = true,
  showFeelsLike = true,
  showWind = false,
  showHumidity = false,
  className,
}) => {
  const { t } = useTranslation(["common", "weather"]);
  const { temperatureUnit, isMetric } = useUserProfileContext();

  const hasWeatherData = useMemo(() => {
    return (
      data.weatherTempC !== null &&
      data.weatherTempC !== undefined
    );
  }, [data.weatherTempC]);

  const useCelsius = temperatureUnit === "C";

  const temperature = useMemo(
    () => formatTemperature(data.weatherTempC, useCelsius),
    [data.weatherTempC, useCelsius]
  );

  const feelsLike = useMemo(
    () => formatTemperature(data.weatherFeelsLikeC, useCelsius),
    [data.weatherFeelsLikeC, useCelsius]
  );

  const windSpeed = useMemo(
    () => formatWindSpeed(data.weatherWindSpeedMps, isMetric),
    [data.weatherWindSpeedMps, isMetric]
  );

  const windDirection = useMemo(
    () => getWindDirection(data.weatherWindDirDeg),
    [data.weatherWindDirDeg]
  );

  const visibility = useMemo(
    () => formatVisibility(data.weatherVisibilityM, isMetric),
    [data.weatherVisibilityM, isMetric]
  );

  if (!hasWeatherData) {
    return null;
  }

  // Build tooltip content for compact variant
  const tooltipContent = useMemo(() => {
    const parts: string[] = [];

    if (data.weatherDescription) {
      parts.push(data.weatherDescription);
    }
    if (data.weatherFeelsLikeC !== null && data.weatherFeelsLikeC !== undefined) {
      parts.push(`${t("weather:feelsLike", { defaultValue: "Feels like" })}: ${feelsLike}`);
    }
    if (data.weatherHumidityPct !== null && data.weatherHumidityPct !== undefined) {
      parts.push(`${t("weather:humidity", { defaultValue: "Humidity" })}: ${data.weatherHumidityPct}%`);
    }
    if (data.weatherWindSpeedMps !== null && data.weatherWindSpeedMps !== undefined) {
      const windText = windDirection
        ? `${windSpeed} ${windDirection}`
        : windSpeed;
      parts.push(`${t("weather:wind", { defaultValue: "Wind" })}: ${windText}`);
    }

    return parts.join("\n");
  }, [data, feelsLike, windSpeed, windDirection, t]);

  // ============================================
  // Compact Variant - For table cells
  // ============================================
  if (variant === "compact") {
    return (
      <Tooltip
        label={<Text size="xs" style={{ whiteSpace: "pre-line" }}>{tooltipContent}</Text>}
        disabled={!tooltipContent}
        multiline
        w={200}
      >
        <Group gap={6} wrap="nowrap" className={className}>
          {showIcon && (
            <WeatherIconDisplay
              conditionCode={data.weatherConditionCode}
              size={18}
            />
          )}
          <Text size="sm" fw={500}>
            {temperature}
          </Text>
        </Group>
      </Tooltip>
    );
  }

  // ============================================
  // Small Variant - Single horizontal line for cards
  // Icon · Temp · (Feels like) · Description · Wind
  // ============================================
  if (variant === "small") {
    return (
      <Group gap="sm" wrap="wrap" align="center" className={className}>
        {/* Icon + Temperature — always stay together */}
        <Group gap={6} wrap="nowrap" align="center">
          {showIcon && (
            <WeatherIconDisplay
              conditionCode={data.weatherConditionCode}
              size={22}
              description={data.weatherDescription}
            />
          )}
          <Text size="md" fw={600} style={{ whiteSpace: "nowrap" }}>
            {temperature}
          </Text>
        </Group>

        {/* Feels like */}
        {showFeelsLike &&
          data.weatherFeelsLikeC !== null &&
          data.weatherFeelsLikeC !== undefined &&
          data.weatherFeelsLikeC !== data.weatherTempC && (
            <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              ({t("weather:feelsLike", { defaultValue: "feels" })} {feelsLike})
            </Text>
          )}

        {/* Description — inline */}
        {data.weatherDescription && (
          <Text size="sm" c="dimmed" lineClamp={1}>
            {data.weatherDescription}
          </Text>
        )}

        {/* Wind — inline */}
        {showWind &&
          data.weatherWindSpeedMps !== null &&
          data.weatherWindSpeedMps !== undefined &&
          data.weatherWindSpeedMps > 0 && (
            <Group gap={4} wrap="nowrap" align="center">
              <AppIcon iconName="Wind" size={16} color="teal" />
              <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                {windSpeed}{windDirection ? ` ${windDirection}` : ""}
              </Text>
            </Group>
          )}

        {/* Humidity — inline (optional) */}
        {showHumidity &&
          data.weatherHumidityPct !== null &&
          data.weatherHumidityPct !== undefined && (
            <Group gap={4} wrap="nowrap" align="center">
              <AppIcon iconName="Droplet" size={16} color="blue" />
              <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                {data.weatherHumidityPct}%
              </Text>
            </Group>
          )}
      </Group>
    );
  }

  // ============================================
  // Full Variant - For detail views
  // ============================================
  return (
    <Paper p="md" withBorder radius="md" className={className}>
      <Stack gap="md">
        {/* Main temperature and condition */}
        <Group gap="md" wrap="nowrap" align="flex-start">
          {showIcon && (
            <ThemeIcon
              size={48}
              radius="md"
              variant="light"
              color={getWeatherIcon(data.weatherConditionCode).color}
            >
              <WeatherIconDisplay
                conditionCode={data.weatherConditionCode}
                size={28}
              />
            </ThemeIcon>
          )}
          <Stack gap={4}>
            <Group gap="sm" align="baseline">
              <Text size="xl" fw={700}>
                {temperature}
              </Text>
              {showFeelsLike &&
                data.weatherFeelsLikeC !== null &&
                data.weatherFeelsLikeC !== undefined && (
                  <Text size="sm" c="dimmed">
                    {t("weather:feelsLike", { defaultValue: "Feels like" })} {feelsLike}
                  </Text>
                )}
            </Group>
            {data.weatherDescription && (
              <Text size="md" c="dimmed">
                {data.weatherDescription}
              </Text>
            )}
          </Stack>
        </Group>

        {/* Additional weather details */}
        <Group gap="xl" wrap="wrap">
          {(showHumidity || variant === "full") &&
            data.weatherHumidityPct !== null &&
            data.weatherHumidityPct !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Droplet" size={18} color="blue" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:humidity", { defaultValue: "Humidity" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {data.weatherHumidityPct}%
                  </Text>
                </Stack>
              </Group>
            )}

          {(showWind || variant === "full") &&
            data.weatherWindSpeedMps !== null &&
            data.weatherWindSpeedMps !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Wind" size={18} color="teal" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:wind", { defaultValue: "Wind" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {windSpeed} {windDirection}
                  </Text>
                </Stack>
              </Group>
            )}

          {variant === "full" &&
            data.weatherVisibilityM !== null &&
            data.weatherVisibilityM !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Eye" size={18} color="gray" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:visibility", { defaultValue: "Visibility" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {visibility}
                  </Text>
                </Stack>
              </Group>
            )}

          {variant === "full" &&
            data.weatherCloudPct !== null &&
            data.weatherCloudPct !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Cloud" size={18} color="gray" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:clouds", { defaultValue: "Clouds" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {data.weatherCloudPct}%
                  </Text>
                </Stack>
              </Group>
            )}

          {variant === "full" &&
            data.weatherPrecipMm !== null &&
            data.weatherPrecipMm !== undefined &&
            data.weatherPrecipMm > 0 && (
              <Group gap={8}>
                <AppIcon iconName="CloudRain" size={18} color="blue" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:precipitation", { defaultValue: "Precipitation" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {data.weatherPrecipMm.toFixed(1)} mm
                  </Text>
                </Stack>
              </Group>
            )}

          {variant === "full" &&
            data.weatherUvIndex !== null &&
            data.weatherUvIndex !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Sun" size={18} color="yellow" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:uvIndex", { defaultValue: "UV Index" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {data.weatherUvIndex}
                  </Text>
                </Stack>
              </Group>
            )}

          {variant === "full" &&
            data.weatherPressureHpa !== null &&
            data.weatherPressureHpa !== undefined && (
              <Group gap={8}>
                <AppIcon iconName="Gauge" size={18} color="gray" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {t("weather:pressure", { defaultValue: "Pressure" })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {data.weatherPressureHpa} hPa
                  </Text>
                </Stack>
              </Group>
            )}
        </Group>

        {data.weatherProvider && (
          <Text size="xs" c="dimmed" ta="right">
            {t("weather:dataFrom", { defaultValue: "Weather data from" })} {data.weatherProvider}
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

export const WeatherDisplay = React.memo(WeatherDisplayComponent);

// ============================================
// Utility Exports
// ============================================

export { getWeatherIcon, formatTemperature, formatWindSpeed, getWindDirection };